import { IconButton, TableCell, TableRow, Tooltip } from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import classNames from 'classnames';
import * as React from 'react';
import type { PageableTableRow } from '../PageableTable';
import { useStyles } from '../styles';

interface Props {
  row: PageableTableRow;
  collapsedRowHeight: number;
}

export const CollapsibleTableRow: React.FC<Props> = ({
  row,
  collapsedRowHeight,
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = React.useState(false);
  // kept as a joined string so re-measuring to the same result doesn't re-render
  const [clippedIndexes, setClippedIndexes] = React.useState('');
  const rowRef = React.useRef<HTMLTableRowElement>(null);
  const contentRefs = React.useRef<Array<HTMLDivElement | null>>([]);

  const measure = React.useCallback(() => {
    setClippedIndexes(
      contentRefs.current
        .map((el, index) =>
          el && el.scrollHeight > collapsedRowHeight ? index : '',
        )
        .filter((index) => index !== '')
        .join(','),
    );
  }, [collapsedRowHeight]);

  React.useEffect(() => {
    measure();
  });

  React.useEffect(() => {
    const rowEl = rowRef.current;
    // a window resize reflows the text, which changes what fits
    if (!rowEl || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(rowEl);
    return () => observer.disconnect();
  }, [measure]);

  const clipped = React.useMemo(
    () => new Set(clippedIndexes.split(',').filter(Boolean)),
    [clippedIndexes],
  );

  // only rows that actually lose content to the clamp earn a toggle
  const collapsible = clipped.size > 0;
  const collapsed = collapsible && !expanded;

  return (
    <TableRow hover tabIndex={-1} ref={rowRef} {...row.props}>
      {row.cells.map(({ display, key: cellKey, props }, cellIndex) => (
        <TableCell
          key={cellKey}
          {...props}
          className={classNames(classes.collapsibleCell, props?.className)}
        >
          <div
            ref={(el) => {
              contentRefs.current[cellIndex] = el;
            }}
            className={
              collapsed && clipped.has(String(cellIndex))
                ? classes.clampedCellContent
                : undefined
            }
            style={collapsed ? { maxHeight: collapsedRowHeight } : undefined}
          >
            {display}
          </div>
        </TableCell>
      ))}
      <TableCell className={classes.collapseToggleCell}>
        {collapsible && (
          <Tooltip title={expanded ? 'Show less' : 'Show more'}>
            <IconButton
              size="small"
              aria-label={expanded ? 'Show less' : 'Show more'}
              aria-expanded={expanded}
              onClick={() => setExpanded((e) => !e)}
            >
              {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
};
